import { useState, useEffect } from "react";
import { enrollmentApi } from "../lib";
import { showError } from "../lib/toast";
import {
  Users,
  BookOpen,
  DollarSign,
  TrendingUp,
  Loader,
  Search,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Award,
} from "lucide-react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COLORS = ["#F59E0B", "#10B981", "#3B82F6", "#8B5CF6", "#EF4444"];

interface AnalyticsData {
  summary: {
    totalCourses: number;
    totalEnrollments: number;
    activeEnrollments: number;
    completedEnrollments: number;
    totalRevenue: number;
    averageEnrollmentsPerCourse: number;
  };
  enrollmentsOverTime: { _id: string; count: number }[];
  courseStats: {
    courseId: string;
    courseTitle: string;
    courseThumbnail?: string;
    totalEnrollments: number;
    completedEnrollments: number;
    completionRate: number;
    revenue: number;
  }[];
}

interface Student {
  _id: string;
  user: {
    _id: string;
    username: string;
    email: string;
    avatar?: string;
  };
  course: {
    _id: string;
    title: string;
    thumbnail?: string;
  };
  progress: number;
  status: string;
  enrolledAt: string;
  completedAt?: string;
}

const InstructorAnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsPage, setStudentsPage] = useState(1);
  const [studentsTotal, setStudentsTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");

  useEffect(() => {
    fetchAnalytics();
    fetchStudents();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await enrollmentApi.getInstructorAnalytics();
      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (err: any) {
      console.error("Failed to fetch analytics:", err);
      showError("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async (page = 1) => {
    try {
      setStudentsLoading(true);
      const response = await enrollmentApi.getInstructorStudents({
        page,
        limit: 10,
        search: searchTerm || undefined,
        courseId: selectedCourse || undefined,
      });
      if (response.success) {
        setStudents(response.data);
        setStudentsTotal(response.pagination?.total || 0);
        setStudentsPage(page);
      }
    } catch (err: any) {
      console.error("Failed to fetch students:", err);
    } finally {
      setStudentsLoading(false);
    }
  };

  const handleSearch = () => {
    fetchStudents(1);
  };

  const handleCourseFilter = (courseId: string) => {
    setSelectedCourse(courseId);
    fetchStudents(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <Loader className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("fr-CM", {
      style: "currency",
      currency: "XAF",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-1">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 shadow-sm">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-lg font-bold text-gray-900 md:text-xl">
              Analytics Dashboard
            </h1>
          </div>
          <p className="text-[10px] text-gray-500 ml-10 md:text-xs">
            Track your course performance and student engagement
          </p>
        </div>

        {/* Stats Grid */}
        {analytics && (
          <div className="grid grid-cols-2 gap-3 mb-5 sm:grid-cols-4 lg:gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[9px] text-gray-500 md:text-xs">
                    Total Courses
                  </p>
                  <p className="mt-0.5 text-sm font-bold text-gray-900 md:text-base">
                    {analytics.summary.totalCourses}
                  </p>
                </div>
                <div className="p-1.5 rounded-lg bg-blue-100 md:p-2">
                  <BookOpen className="w-3.5 h-3.5 text-blue-600 md:w-4 md:h-4" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[9px] text-gray-500 md:text-xs">
                    Enrollments
                  </p>
                  <p className="mt-0.5 text-sm font-bold text-gray-900 md:text-base">
                    {analytics.summary.totalEnrollments}
                  </p>
                </div>
                <div className="p-1.5 rounded-lg bg-purple-100 md:p-2">
                  <Users className="w-3.5 h-3.5 text-purple-600 md:w-4 md:h-4" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[9px] text-gray-500 md:text-xs">Active</p>
                  <p className="mt-0.5 text-sm font-bold text-gray-900 md:text-base">
                    {analytics.summary.activeEnrollments}
                  </p>
                </div>
                <div className="p-1.5 rounded-lg bg-green-100 md:p-2">
                  <TrendingUp className="w-3.5 h-3.5 text-green-600 md:w-4 md:h-4" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[9px] text-gray-500 md:text-xs">Revenue</p>
                  <p className="mt-0.5 text-sm font-bold text-gray-900 md:text-base">
                    {formatCurrency(analytics.summary.totalRevenue)}
                  </p>
                </div>
                <div className="p-1.5 rounded-lg bg-yellow-100 md:p-2">
                  <DollarSign className="w-3.5 h-3.5 text-yellow-600 md:w-4 md:h-4" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Charts Section */}
        {analytics && (
          <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-2">
            {/* Enrollment Trends Line Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-4">
              <h2 className="text-[11px] font-semibold text-gray-900 mb-2 md:text-sm md:mb-3">
                Enrollment Trends
              </h2>
              <div className="h-40 md:h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={
                      analytics.enrollmentsOverTime?.map((item) => ({
                        date: new Date(item._id).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        }),
                        enrollments: item.count,
                      })) || []
                    }
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 9 }}
                      stroke="#9ca3af"
                    />
                    <YAxis tick={{ fontSize: 9 }} stroke="#9ca3af" />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="enrollments"
                      stroke="#F59E0B"
                      strokeWidth={2}
                      dot={{ fill: "#F59E0B", r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Revenue by Course Bar Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-4">
              <h2 className="text-[11px] font-semibold text-gray-900 mb-2 md:text-sm md:mb-3">
                Revenue by Course
              </h2>
              <div className="h-40 md:h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={
                      analytics.courseStats?.slice(0, 5).map((course) => ({
                        name: course.courseTitle?.substring(0, 12) || "Course",
                        revenue: course.revenue || 0,
                      })) || []
                    }
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 9 }}
                      stroke="#9ca3af"
                    />
                    <YAxis
                      tick={{ fontSize: 9 }}
                      stroke="#9ca3af"
                      tickFormatter={(value) => `${value / 1000}k`}
                    />
                    <Tooltip
                      formatter={(value: number) => [
                        formatCurrency(value),
                        "Revenue",
                      ]}
                    />
                    <Bar
                      dataKey="revenue"
                      fill="#10B981"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Course Distribution Pie Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-4">
              <h2 className="text-[11px] font-semibold text-gray-900 mb-2 md:text-sm md:mb-3">
                Course Distribution
              </h2>
              <div className="h-40 md:h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={
                        analytics.courseStats?.slice(0, 5).map((course) => ({
                          name:
                            course.courseTitle?.substring(0, 12) || "Course",
                          value: course.totalEnrollments || 0,
                        })) || []
                      }
                      cx="50%"
                      cy="50%"
                      innerRadius={25}
                      outerRadius={45}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {analytics.courseStats?.slice(0, 5).map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 9 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Completion Rate Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-4">
              <h2 className="text-[11px] font-semibold text-gray-900 mb-2 md:text-sm md:mb-3">
                Completion Rates
              </h2>
              <div className="h-40 md:h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={
                      analytics.courseStats?.slice(0, 5).map((course) => ({
                        name: course.courseTitle?.substring(0, 12) || "Course",
                        rate: course.completionRate || 0,
                      })) || []
                    }
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      type="number"
                      domain={[0, 100]}
                      tick={{ fontSize: 9 }}
                      stroke="#9ca3af"
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{ fontSize: 9 }}
                      stroke="#9ca3af"
                      width={80}
                    />
                    <Tooltip
                      formatter={(value: number) => [
                        `${value.toFixed(1)}%`,
                        "Completion Rate",
                      ]}
                    />
                    <Bar dataKey="rate" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Course Performance Table */}
        {analytics && analytics.courseStats.length > 0 && (
          <div className="mb-5 overflow-hidden bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white md:p-4">
              <h2 className="text-xs font-semibold text-gray-900 md:text-sm">
                Course Performance
              </h2>
            </div>

            {/* Desktop Table View */}
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-[11px] font-semibold text-left text-gray-600 uppercase">
                      Course
                    </th>
                    <th className="px-4 py-3 text-[11px] font-semibold text-left text-gray-600 uppercase">
                      Enroll
                    </th>
                    <th className="px-4 py-3 text-[11px] font-semibold text-left text-gray-600 uppercase">
                      Completed
                    </th>
                    <th className="px-4 py-3 text-[11px] font-semibold text-left text-gray-600 uppercase">
                      Rate
                    </th>
                    <th className="px-4 py-3 text-[11px] font-semibold text-left text-gray-600 uppercase">
                      Revenue
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {analytics.courseStats.map((course) => (
                    <tr
                      key={course.courseId}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {course.courseThumbnail && (
                            <img
                              src={course.courseThumbnail}
                              alt=""
                              className="object-cover w-8 h-8 rounded"
                            />
                          )}
                          <p className="text-xs font-medium text-gray-900 truncate max-w-[200px]">
                            {course.courseTitle}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {course.totalEnrollments}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {course.completedEnrollments}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-green-500 rounded-full"
                              style={{ width: `${course.completionRate}%` }}
                            />
                          </div>
                          <span className="text-[10px] text-gray-600">
                            {course.completionRate.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs font-semibold text-gray-900">
                        {formatCurrency(course.revenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="divide-y divide-gray-100 md:hidden">
              {analytics.courseStats.map((course) => (
                <div
                  key={course.courseId}
                  className="p-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    {course.courseThumbnail && (
                      <img
                        src={course.courseThumbnail}
                        alt=""
                        className="object-cover w-10 h-10 rounded-lg"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-900 truncate">
                        {course.courseTitle}
                      </p>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                          <p className="text-[8px] text-gray-500">
                            Enrollments
                          </p>
                          <p className="text-[11px] font-semibold text-gray-900">
                            {course.totalEnrollments}
                          </p>
                        </div>
                        <div>
                          <p className="text-[8px] text-gray-500">Completed</p>
                          <p className="text-[11px] font-semibold text-green-600">
                            {course.completedEnrollments}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-[8px] text-gray-500">
                            Completion Rate
                          </span>
                          <span className="text-[9px] font-medium text-gray-700">
                            {course.completionRate.toFixed(0)}%
                          </span>
                        </div>
                        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-500 rounded-full"
                            style={{ width: `${course.completionRate}%` }}
                          />
                        </div>
                      </div>
                      <div className="mt-2 pt-1 border-t border-gray-100">
                        <p className="text-[9px] text-gray-500">Revenue</p>
                        <p className="text-xs font-bold text-orange-600">
                          {formatCurrency(course.revenue)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Enrollment Trends Bar Chart */}
        {analytics && analytics.enrollmentsOverTime.length > 0 && (
          <div className="mb-5 bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-4">
            <h2 className="text-[11px] font-semibold text-gray-900 mb-2 md:text-sm md:mb-3">
              Enrollment Trends (Last 14 Days)
            </h2>
            <div className="flex items-end h-24 gap-1 sm:h-28 md:h-32">
              {analytics.enrollmentsOverTime.slice(-14).map((day, idx) => {
                const maxCount = Math.max(
                  ...analytics.enrollmentsOverTime.map((d) => d.count),
                  1,
                );
                const height = (day.count / maxCount) * 100;
                return (
                  <div
                    key={idx}
                    className="flex flex-col items-center flex-1 gap-0.5"
                  >
                    <div
                      className="w-full transition-all rounded-t bg-gradient-to-t from-orange-500 to-orange-400 hover:from-orange-600 hover:to-orange-500"
                      style={{
                        height: `${height}%`,
                        minHeight: day.count > 0 ? "2px" : "0",
                      }}
                    />
                    <span className="text-[7px] text-gray-400 sm:text-[8px] md:text-[9px]">
                      {new Date(day._id).getDate()}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recent Students Section */}
        <div className="overflow-hidden bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white md:p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-xs font-semibold text-gray-900 md:text-sm">
                Recent Students
              </h2>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    className="pl-8 pr-3 py-1.5 text-[10px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 w-full sm:w-40 md:text-xs"
                  />
                </div>

                {analytics && analytics.courseStats.length > 0 && (
                  <select
                    value={selectedCourse}
                    onChange={(e) => handleCourseFilter(e.target.value)}
                    className="px-3 py-1.5 text-[10px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 md:text-xs"
                  >
                    <option value="">All Courses</option>
                    {analytics.courseStats.map((course) => (
                      <option key={course.courseId} value={course.courseId}>
                        {course.courseTitle}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>

          {studentsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-5 h-5 text-orange-500 animate-spin" />
            </div>
          ) : students.length === 0 ? (
            <div className="py-12 text-center">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-gray-100 to-gray-200">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-[11px] text-gray-500">No students found</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-[11px] font-semibold text-left text-gray-600 uppercase">
                        Student
                      </th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-left text-gray-600 uppercase">
                        Course
                      </th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-left text-gray-600 uppercase">
                        Progress
                      </th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-left text-gray-600 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-left text-gray-600 uppercase">
                        Enrolled
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {students.map((student) => (
                      <tr
                        key={student._id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center">
                              {student.user.avatar ? (
                                <img
                                  src={student.user.avatar}
                                  alt=""
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              ) : (
                                <span className="text-xs font-semibold text-orange-600">
                                  {student.user.username
                                    ?.charAt(0)
                                    .toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-900">
                                {student.user.username}
                              </p>
                              <p className="text-[10px] text-gray-500 truncate max-w-[150px]">
                                {student.user.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-xs text-gray-600 truncate max-w-[180px]">
                            {student.course.title}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-orange-500 to-orange-600 rounded-full"
                                style={{ width: `${student.progress}%` }}
                              />
                            </div>
                            <span className="text-[10px] font-medium text-gray-600">
                              {student.progress}%
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-semibold ${
                              student.status === "completed"
                                ? "bg-green-100 text-green-700"
                                : student.status === "active"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {student.status.charAt(0).toUpperCase() +
                              student.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-[10px] text-gray-500 whitespace-nowrap">
                            {formatDate(student.enrolledAt)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="divide-y divide-gray-100 md:hidden">
                {students.map((student) => (
                  <div
                    key={student._id}
                    className="p-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center flex-shrink-0">
                        {student.user.avatar ? (
                          <img
                            src={student.user.avatar}
                            alt=""
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-sm font-semibold text-orange-600">
                            {student.user.username?.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-xs font-semibold text-gray-900">
                              {student.user.username}
                            </p>
                            <p className="text-[9px] text-gray-500 truncate">
                              {student.user.email}
                            </p>
                          </div>
                          <span
                            className={`inline-flex px-1.5 py-0.5 rounded-full text-[8px] font-semibold ${
                              student.status === "completed"
                                ? "bg-green-100 text-green-700"
                                : student.status === "active"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {student.status.charAt(0).toUpperCase() +
                              student.status.slice(1)}
                          </span>
                        </div>
                        <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                          <p className="text-[9px] font-medium text-gray-700 truncate">
                            {student.course.title}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            <div className="flex items-center gap-1">
                              <div className="w-16 h-1 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-orange-500 to-orange-600 rounded-full"
                                  style={{ width: `${student.progress}%` }}
                                />
                              </div>
                              <span className="text-[9px] font-medium text-gray-600">
                                {student.progress}%
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-2.5 h-2.5 text-gray-400" />
                              <span className="text-[8px] text-gray-500">
                                {formatDate(student.enrolledAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Pagination */}
          {studentsTotal > 10 && (
            <div className="p-3 border-t border-gray-100 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[9px] text-gray-500 text-center sm:text-left">
                Showing {students.length} of {studentsTotal} students
              </p>
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => fetchStudents(studentsPage - 1)}
                  disabled={studentsPage === 1}
                  className="flex items-center gap-0.5 px-2.5 py-1 text-[9px] font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  <ChevronLeft className="w-3 h-3" />
                  Prev
                </button>
                <span className="text-[9px] text-gray-600">
                  Page {studentsPage} of {Math.ceil(studentsTotal / 10)}
                </span>
                <button
                  onClick={() => fetchStudents(studentsPage + 1)}
                  disabled={studentsPage * 10 >= studentsTotal}
                  className="flex items-center gap-0.5 px-2.5 py-1 text-[9px] font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Next
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Stats Summary Footer */}
        {analytics && (
          <div className="grid grid-cols-3 gap-3 mt-5">
            <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
              <Award className="w-4 h-4 mx-auto mb-0.5 text-orange-500" />
              <p className="text-[10px] font-bold text-gray-900">
                {analytics.summary.averageEnrollmentsPerCourse || 0}
              </p>
              <p className="text-[7px] text-gray-500">Avg/Course</p>
            </div>
            <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
              <Users className="w-4 h-4 mx-auto mb-0.5 text-blue-500" />
              <p className="text-[10px] font-bold text-gray-900">
                {(
                  (analytics.summary.completedEnrollments /
                    analytics.summary.totalEnrollments) *
                    100 || 0
                ).toFixed(0)}
                %
              </p>
              <p className="text-[7px] text-gray-500">Completion</p>
            </div>
            <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
              <TrendingUp className="w-4 h-4 mx-auto mb-0.5 text-green-500" />
              <p className="text-[10px] font-bold text-gray-900">
                {analytics.summary.activeEnrollments}
              </p>
              <p className="text-[7px] text-gray-500">Active Now</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstructorAnalyticsPage;
